import {
  Col,
  Row,
  Space,
  Table,
  Tag,
  Card,
  Button,
  message,
  Spin,
  Input,
  Select,
} from "antd";
import { useEffect, useState, useCallback, useMemo } from "react";
import {
  ReloadOutlined,
  BankOutlined,
  CloudUploadOutlined,
} from "@ant-design/icons";
import {
  HEADERS_EXPORT_BEN,
  POST_LOAN_DB2,
  POST_MAIN_LAWYER,
} from "../../API/apiUrls";
import axios from "axios";
import dayjs from "dayjs";
import MotionHoc from "../../../utils/MotionHoc";
import CreateJudgment from "./componentsJudgment/CreateJudgment";
import AddImage from "../contractDetail/modal/AddImage";

const { Option } = Select;

const Main = () => {
  const userId = parseInt(localStorage.getItem("USER_ID"));

  // States
  const [dataArr, setDataArr] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInputTable, setSearchInputTable] = useState("");
  const [processStatus, setProcessStatus] = useState(5);
  const [dataRecord, setDataRecord] = useState(null);
  const [showCreateJudgment, setShowCreateJudgment] = useState(false); // ✅ State สำหรับเปลี่ยนหน้า
  const [isModalAddImge, setIsModalAddImge] = useState(false);
  const [loanDataDB2, setLoanDataDB2] = useState([]);

  // ✅ โหลดข้อมูลหลัก
  const loadMain = useCallback(async () => {
    setLoading(true);
    let isMounted = true;
    const controller = new AbortController();

    try {
      const res = await axios.post(
        POST_MAIN_LAWYER,
        { userId: userId },
        {
          headers: HEADERS_EXPORT_BEN,
          signal: controller.signal,
        },
      );

      if (!isMounted) return;

      if (res.data && res.data.length > 0) {
        const filteredData = res.data.filter(
          (item) =>
            item.currentStatus === 5 ||
            item.currentStatus === 9 ||
            item.currentStatus === 10 ||
            item.currentStatus === 8,
        );
        console.log("filteredData", filteredData);

        setDataArr(filteredData);
      } else {
        setDataArr([]);
        message.info("ไม่มีข้อมูล");
      }
    } catch (error) {
      if (error.name !== "CanceledError" && isMounted) {
        console.error("❌ Error loading data:", error);
        const errorMsg =
          error.response?.data?.message ||
          error.message ||
          "ไม่สามารถโหลดข้อมูลได้";
        message.error(errorMsg);
        setDataArr([]);
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  const loadLoanData = async (contno) => {
    setLoading(true);
    const useContno = /^V\d/.test(contno) ? contno.substring(1) : contno;

    try {
      const res = await axios.post(
        POST_LOAN_DB2,
        { CONTNO: `'${useContno}'` },
        { headers: HEADERS_EXPORT_BEN },
      );
      if (res?.data?.[0]) setLoanDataDB2(res.data?.[0]);
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("Error loadLoanData:", error);
        message.error("เกิดข้อผิดพลาดในการโหลดข้อมูลสัญญา");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMain();
  }, [loadMain]);

  // ✅ ฟังก์ชันสำหรับสร้าง Options ของ Select
  const renderOptionStatus = useCallback(() => {
    const statusOptions = [
      { value: 99, icon: "🗂️", label: "ทั้งหมด" },
      { value: 5, icon: "⚖️", label: "รอพิพากษา" },
      { value: 9, icon: "🔨", label: "พิพากษา" },
      { value: 10, icon: "🤝", label: "ทำยอม" },
      { value: 8, icon: "🚫", label: "ถอนฟ้อง" },
    ];
    return statusOptions.map(({ value, icon, label }) => (
      <Option key={value} value={value}>
        <span style={{ marginRight: 8 }}>{icon}</span>
        {label}
      </Option>
    ));
  }, []);

  // ✅ กรองข้อมูลอัตโนมัติด้วย useMemo
  const arrayTable = useMemo(() => {
    if (!dataArr || dataArr.length === 0) return [];

    const normalize = (value) =>
      String(value ?? "")
        .trim()
        .toLowerCase();

    const searchQuery = normalize(searchInputTable);

    return dataArr.filter((item) => {
      const passQuery =
        !searchQuery ||
        [
          item.CUSCOD,
          item.CONTNO,
          item.NAME1,
          item.NAME2,
          `${item.SNAM || ""}${item.NAME1 || ""} ${item.NAME2 || ""}`,
        ]
          .map(normalize)
          .some((val) => val.includes(searchQuery));

      let passStatus = true;
      if (processStatus !== 99 && processStatus != null) {
        passStatus = item.currentStatus === Number(processStatus);
      }

      return passQuery && passStatus;
    });
  }, [dataArr, searchInputTable, processStatus]);

  // ✅ ฟังก์ชันล้างฟิลเตอร์
  const handleResetFilter = useCallback(() => {
    setSearchInputTable("");
    setProcessStatus(99);
    message.info("ล้างตัวกรองสำเร็จ");
  }, []);

  // ✅ เปิดหน้า CreateJudgment
  const handleOpenJudgment = useCallback((record) => {
    loadLoanData(record.CONTNO);
    console.log("ลงคำพิพากษา:", record);
    setDataRecord(record);
    setShowCreateJudgment(true);
  }, []);

  // ✅ ปิดหน้า CreateJudgment และกลับมาที่หน้า Table
  const handleCloseJudgment = useCallback(() => {
    setShowCreateJudgment(false);
    setDataRecord(null);
    loadMain(); // โหลดข้อมูลใหม่
  }, [loadMain]);

  // ✅ Columns สำหรับตาราง
  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 80,
      render: (_, __, index) => index + 1,
    },
    {
      title: "เลขสัญญา",
      dataIndex: "CONTNO",
      key: "CONTNO",
      align: "center",
      width: 150,
    },
    {
      title: "ชื่อ-นามสกุล",
      key: "fullName",
      align: "center",
      render: (_, record) => (
        <span>
          {`${record.SNAM || ""}${record.NAME1 || ""} ${
            record.NAME2 || ""
          }`.trim()}
        </span>
      ),
    },
    {
      title: "ศาล",
      key: "provincialCourt",
      align: "center",
      render: (_, record) => <span>{record.provincialCourt}</span>,
    },
    {
      title: "สถานะ",
      key: "status",
      align: "center",
      width: 160,
      render: (_, record) => {
        const { currentStatus } = record;
        const statusMap = {
          7: { color: "red", label: "รอสร้างคำฟ้อง" },
          4: { color: "orange", label: "รอลงวันนัดศาล" },
          5: { color: "blue", label: "รอพิพากษา" },
          9: { color: "green", label: "พิพากษา" },
          10: { color: "purple", label: "ทำยอม" },
          8: { color: "red", label: "ถอนฟ้อง" },
        };

        const status = statusMap[currentStatus] || {
          color: "default",
          label: "ไม่ระบุ",
        };

        return (
          <Tag color={status.color}>
            <div style={{ textAlign: "center" }}>
              <div>{status.label}</div>
              {currentStatus === 5 && record.considerationDate && (
                <div style={{ fontSize: 11, marginTop: 4 }}>
                  {dayjs(record.considerationDate)
                    .add(543, "year")
                    .format("D MMM YY")}
                </div>
              )}

              {currentStatus === 9 && record.dtJudge && (
                <div style={{ fontSize: 11, marginTop: 4 }}>
                  {dayjs(record.dtJudge).add(543, "year").format("D MMM YY")}
                </div>
              )}
              {currentStatus === 10 && record.dtJudgeSettlement && (
                <div style={{ fontSize: 11, marginTop: 4 }}>
                  {dayjs(record.dtJudgeSettlement)
                    .add(543, "year")
                    .format("D MMM YY")}
                </div>
              )}
              {currentStatus === 8 && record.dtOfWithdrawLawsult && (
                <div style={{ fontSize: 11, marginTop: 4 }}>
                  {dayjs(record.dtOfWithdrawLawsult)
                    .add(543, "year")
                    .format("D MMM YY")}
                </div>
              )}
            </div>
          </Tag>
        );
      },
    },
    processStatus !== 8 && {
      title: "การจัดการ",
      key: "actions",
      align: "center",
      width: 150,
      render: (_, record) => {
        const { currentStatus } = record;

        if (currentStatus === 5) {
          return (
            <Button
              color="blue"
              variant="outlined"
              icon={<BankOutlined />}
              onClick={() => {
                handleOpenJudgment(record);
              }}
              block
            >
              ลงคำพิพากษา
            </Button>
          );
        }

        return (
          <Button
            color="orange"
            variant="outlined"
            icon={<CloudUploadOutlined />}
            onClick={() => {
              setDataRecord(record);
              setIsModalAddImge(true);
            }}
            block
          >
            อัพโหลดเพิ่มเติม
          </Button>
        );
      },
    },
  ].filter(Boolean);

  // ✅ แสดง CreateJudgment แทน Table
  if (showCreateJudgment && dataRecord && loanDataDB2) {
    return (
      <CreateJudgment
        dataDefault={dataRecord}
        onClose={handleCloseJudgment}
        funcUpdateStatus={handleCloseJudgment}
        dataDB2={loanDataDB2}
      />
    );
  }

  // ✅ แสดง Table
  return (
    <Card>
      <Spin spinning={loading} size="large" tip="กำลังโหลดข้อมูล...">
        <Row gutter={[0, 16]}>
          {/* Filters */}
          <Col span={24}>
            <Space size={10} wrap>
              <Select
                size="large"
                style={{ minWidth: 180 }}
                placeholder="เลือกสถานะ"
                value={processStatus}
                onChange={setProcessStatus}
                showSearch
                optionFilterProp="children"
              >
                {renderOptionStatus()}
              </Select>

              <Input
                placeholder="ค้นหาเลขสัญญา, ชื่อ, เลขบัตร"
                value={searchInputTable}
                onChange={(e) => setSearchInputTable(e.target.value)}
                style={{ width: 250, borderRadius: 8 }}
                size="small"
                allowClear
                suffix={<span style={{ marginRight: 5 }}>🔍</span>}
              />

              <Button
                icon={<ReloadOutlined />}
                onClick={handleResetFilter}
                size="large"
                style={{
                  borderRadius: 8,
                  backgroundColor: "#fff1f0",
                  borderColor: "#ffa39e",
                  color: "#cf1322",
                }}
              >
                ล้างตัวกรอง
              </Button>
            </Space>
          </Col>

          {/* Table */}
          <Col span={24}>
            <Table
              size="small"
              columns={columns}
              dataSource={arrayTable}
              rowKey={(record) => record.CONTNO || record.id}
              scroll={{ x: 1000 }}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `ทั้งหมด ${total} รายการ`,
                pageSizeOptions: ["10", "20", "50", "100"],
              }}
              footer={() => (
                <div style={{ textAlign: "left", fontWeight: 500 }}>
                  จำนวนสัญญาทั้งหมด: {arrayTable?.length || 0} รายการ
                </div>
              )}
            />
          </Col>
        </Row>
        {isModalAddImge && (
          <AddImage
            open={isModalAddImge}
            close={() => setIsModalAddImge(false)}
            dataDefault={dataRecord}
          />
        )}
      </Spin>
    </Card>
  );
};

const MainCreateJudgment = MotionHoc(Main);
export default MainCreateJudgment;
